import { useState, useEffect, useMemo } from "react";
import { useApp } from "../context/AppContext";

const scoreColor = (v) => v >= 90 ? "#0cce6b" : v >= 50 ? "#ffa400" : "#ff4e42";
const scoreBg = (v, dark) => v >= 90 ? (dark ? "rgba(12,206,107,0.1)" : "rgba(12,206,107,0.06)") : v >= 50 ? (dark ? "rgba(255,164,0,0.1)" : "rgba(255,164,0,0.06)") : (dark ? "rgba(255,78,66,0.1)" : "rgba(255,78,66,0.06)");

const ScoreCircle = ({ value, size = 52 }) => {
  const color = scoreColor(value);
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(128,128,128,0.15)" strokeWidth={4} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={4}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central"
        style={{ transform: "rotate(90deg)", transformOrigin: "center", fontSize: size * 0.3, fontWeight: 800, fill: color }}>{value}</text>
    </svg>
  );
};

export default function PageSpeedView() {
  const { proj, activeId, getStore, apiFetch, flash, dark, t, card, inp, btnP, btnG, btnSm, stitle, muted } = useApp();
  if (!proj) return null;
  const store = getStore(activeId);
  const clusters = store.clusters || [];

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [progress, setProgress] = useState(null);
  const [singleUrl, setSingleUrl] = useState(proj?.domain || "");
  const [singleLoading, setSingleLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [sortCol, setSortCol] = useState("performance");
  const [sortDir, setSortDir] = useState("asc");
  const [realVitals, setRealVitals] = useState([]);
  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    apiFetch(`/api/projects/${activeId}/pagespeed`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setResults(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
    apiFetch(`/api/extension/web-vitals/${activeId}`).then(r => r.json()).then(d => { if (Array.isArray(d)) setRealVitals(d); }).catch(() => {});
  }, [activeId, apiFetch]);

  const checkOne = async () => {
    if (!singleUrl.trim()) return;
    setSingleLoading(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/pagespeed/check`, {
        method: "POST", body: JSON.stringify({ url: singleUrl.trim() }),
      });
      const d = await r.json();
      if (d.error) { flash(d.error); }
      else if (Array.isArray(d)) {
        const errors = d.filter(x => x.error);
        const ok = d.filter(x => !x.error);
        ok.forEach(item => {
          setResults(prev => {
            const filtered = prev.filter(x => !(x.url === item.url && x.strategy === item.strategy));
            return [{ ...item, project_id: activeId, checked_at: new Date().toISOString() }, ...filtered];
          });
        });
        if (errors.length && !ok.length) flash("Помилка: " + errors[0].error);
        else if (ok.length) {
          const mob = ok.find(x => x.strategy === "mobile");
          const desk = ok.find(x => x.strategy === "desktop");
          flash(`Mobile: ${mob?.performance || "—"} | Desktop: ${desk?.performance || "—"}`);
        }
      }
    } catch (e) { flash("Помилка: " + e.message); }
    setSingleLoading(false);
  };

  const checkAll = async () => {
    setChecking(true);
    setProgress({ current: 0, total: 0 });
    try {
      const r = await apiFetch(`/api/projects/${activeId}/pagespeed/check-all`, { method: "POST" });
      const d = await r.json();
      if (d.ok) {
        setProgress({ current: 0, total: d.total });
        flash(`Перевірка запущена (${d.total} URL). Можна закрити сторінку.`);
        // Poll for completion via bgTasks
        const checkDone = setInterval(async () => {
          try {
            const pr = await apiFetch(`/api/projects/${activeId}/pagespeed`);
            const pd = await pr.json();
            if (Array.isArray(pd)) setResults(pd);
          } catch {}
        }, 5000);
        setTimeout(() => { clearInterval(checkDone); setChecking(false); setProgress(null); }, 300000); // max 5min poll
      } else {
        flash(d.error || "Помилка"); setChecking(false); setProgress(null);
      }
    } catch (e) { flash("Помилка: " + e.message); setChecking(false); setProgress(null); }
  };

  // Track background task progress
  const { bgTasks } = useApp();
  useEffect(() => {
    const psTask = (bgTasks || []).find(bt => bt.projectId === activeId && bt.type === "pagespeed_check");
    if (psTask) {
      if (psTask.status === "running") {
        setChecking(true);
        setProgress(psTask.progress || {});
      } else if (psTask.status === "done") {
        setChecking(false);
        setProgress(null);
        // Reload results
        apiFetch(`/api/projects/${activeId}/pagespeed`).then(r => r.json()).then(d => { if (Array.isArray(d)) setResults(d); }).catch(() => {});
      }
    }
  }, [bgTasks]);

  // Group by URL: { url, mobile: {...}, desktop: {...} }
  const grouped = useMemo(() => {
    const map = {};
    results.forEach(r => {
      if (!map[r.url]) map[r.url] = { url: r.url };
      map[r.url][r.strategy] = r;
    });
    let data = Object.values(map);
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(g => g.url.toLowerCase().includes(q));
    }
    data.sort((a, b) => {
      const av = a.mobile?.[sortCol] || a.desktop?.[sortCol] || 0;
      const bv = b.mobile?.[sortCol] || b.desktop?.[sortCol] || 0;
      return (sortDir === "asc" ? 1 : -1) * (av - bv);
    });
    return data;
  }, [results, search, sortCol, sortDir]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  const avg = (strat, key) => {
    const items = results.filter(r => r.strategy === strat && r[key] != null);
    return items.length ? Math.round(items.reduce((s, r) => s + r[key], 0) / items.length) : 0;
  };

  const stripUrl = (url) => { try { return new URL(url).pathname || "/"; } catch { return url; } };

  const metricCard = (label, value, display, good, mid) => {
    const color = value == null ? t.textMuted : value <= good ? "#0cce6b" : value <= mid ? "#ffa400" : "#ff4e42";
    return (
      <div style={{ flex: 1, minWidth: 80, padding: "8px 10px", borderRadius: 8, background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", textAlign: "center" }}>
        <div style={{ fontSize: 9, color: t.textMuted, marginBottom: 3, fontWeight: 600, textTransform: "uppercase" }}>{label}</div>
        <div style={{ fontSize: 14, fontWeight: 800, color }}>{display || "—"}</div>
      </div>
    );
  };

  const ScoreBadge = ({ value }) => (
    <span style={{
      fontSize: 11, fontWeight: 800, padding: "2px 7px", borderRadius: 6, minWidth: 28, textAlign: "center", display: "inline-block",
      background: scoreBg(value, dark), color: scoreColor(value),
    }}>{value}</span>
  );

  const mobileCount = results.filter(r => r.strategy === "mobile").length;
  const desktopCount = results.filter(r => r.strategy === "desktop").length;

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 14 }}>⚡ PageSpeed Insights</h2>

      {/* Check URL */}
      <div style={{ ...card, padding: 16 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input value={singleUrl} onChange={e => setSingleUrl(e.target.value)} placeholder="https://example.com/page"
            onKeyDown={e => e.key === "Enter" && checkOne()}
            style={{ ...inp, flex: 1, fontSize: 12, padding: "8px 12px" }} />
          <button onClick={checkOne} disabled={singleLoading || !singleUrl.trim()} style={{ ...btnP, opacity: singleLoading ? 0.5 : 1 }}>
            {singleLoading ? "📱+🖥..." : "⚡ Перевірити"}
          </button>
          <button onClick={checkAll} disabled={checking || !clusters.length} style={{ ...btnG, opacity: checking ? 0.5 : 1 }}>
            {checking ? `${progress?.current || 0}/${progress?.total || 0}` : `Всі (${clusters.length})`}
          </button>
          <button onClick={async () => {
            if (!confirm("Зупинити перевірку та видалити всі результати?")) return;
            try {
              await apiFetch(`/api/projects/${activeId}/pagespeed/stop`, { method: "POST" });
              setResults([]); setChecking(false); setProgress(null);
              flash("Зупинено та очищено");
            } catch {}
          }} style={{ ...btnSm, color: "#ef4444", borderColor: "#ef444433" }}>
            🗑 Зупинити + очистити
          </button>
        </div>
        <div style={{ fontSize: 10, color: t.textMuted, marginTop: 6 }}>Перевіряє одразу Mobile + Desktop</div>
        {progress && progress.total > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{progress.current}/{progress.total}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#6366f1" }}>{Math.round((progress.current / progress.total) * 100)}%</span>
              {progress.url && <span style={{ fontSize: 11, color: t.textMuted }}>— {stripUrl(progress.url)}</span>}
            </div>
            <div style={{ height: 6, borderRadius: 3, background: dark ? "#252535" : "#e5e7eb", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${(progress.current / progress.total) * 100}%`, background: "linear-gradient(90deg, #6366f1, #8b5cf6)", borderRadius: 3, transition: "width 0.3s" }} />
            </div>
          </div>
        )}
      </div>

      {/* Average scores: Mobile vs Desktop */}
      {(mobileCount > 0 || desktopCount > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          {["mobile", "desktop"].map(strat => {
            const count = results.filter(r => r.strategy === strat).length;
            if (!count) return <div key={strat} />;
            return (
              <div key={strat} style={{
                ...card, padding: 16, margin: 0,
                border: `1px solid ${strat === "mobile" ? "#6366f1" : "#10b981"}22`,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, textAlign: "center" }}>
                  {strat === "mobile" ? "📱 Mobile" : "🖥 Desktop"} <span style={{ color: t.textMuted, fontWeight: 400 }}>({count})</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-around" }}>
                  {[
                    { key: "performance", label: "Perf" },
                    { key: "accessibility", label: "A11y" },
                    { key: "best_practices", label: "BP" },
                    { key: "seo", label: "SEO" },
                  ].map(c => (
                    <div key={c.key} style={{ textAlign: "center" }}>
                      <ScoreCircle value={avg(strat, c.key)} size={44} />
                      <div style={{ fontSize: 9, color: t.textMuted, marginTop: 2 }}>{c.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Real Web Vitals from Extension */}
      {realVitals.length > 0 && (() => {
        const rvLen = realVitals.length;
        const median = (arr) => { const s = [...arr].sort((a, b) => a - b); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };
        const lcps = realVitals.filter(r => r.lcp > 0).map(r => +r.lcp);
        const clss = realVitals.filter(r => r.cls != null).map(r => +r.cls);
        const fcps = realVitals.filter(r => r.fcp > 0).map(r => +r.fcp);
        const ttfbs = realVitals.filter(r => r.ttfb > 0).map(r => +r.ttfb);
        const vitalCard = (label, values, unit, goodT, badT, isFloat) => {
          if (!values.length) return null;
          const med = median(values);
          const display = isFloat ? med.toFixed(3) : unit === "ms" ? (med > 999 ? (med / 1000).toFixed(1) + "s" : Math.round(med) + "ms") : Math.round(med);
          const color = isFloat ? (med <= goodT ? "#0cce6b" : med <= badT ? "#ffa400" : "#ff4e42") : (med <= goodT ? "#0cce6b" : med <= badT ? "#ffa400" : "#ff4e42");
          const good = values.filter(v => v <= goodT).length;
          const mid = values.filter(v => v > goodT && v <= badT).length;
          const bad = values.filter(v => v > badT).length;
          return (
            <div style={{ ...card, padding: 12, margin: 0, textAlign: "center" }}>
              <div style={{ fontSize: 10, color: t.textMuted, marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color }}>{display}</div>
              <div style={{ fontSize: 9, color: t.textMuted }}>медіана ({values.length})</div>
              <div style={{ display: "flex", gap: 2, marginTop: 6, height: 4, borderRadius: 2, overflow: "hidden" }}>
                {good > 0 && <div style={{ flex: good, background: "#0cce6b" }} />}
                {mid > 0 && <div style={{ flex: mid, background: "#ffa400" }} />}
                {bad > 0 && <div style={{ flex: bad, background: "#ff4e42" }} />}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                <span style={{ fontSize: 8, color: "#0cce6b" }}>{good}</span>
                <span style={{ fontSize: 8, color: "#ffa400" }}>{mid}</span>
                <span style={{ fontSize: 8, color: "#ff4e42" }}>{bad}</span>
              </div>
            </div>
          );
        };
        return (
          <div style={{ ...card, padding: 16, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>🌐 Реальні Web Vitals (Extension, {rvLen} сторінок)</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              {vitalCard("LCP", lcps, "ms", 2500, 4000)}
              {vitalCard("CLS", clss, "", 0.1, 0.25, true)}
              {vitalCard("FCP", fcps, "ms", 1800, 3000)}
              {vitalCard("TTFB", ttfbs, "ms", 200, 800)}
            </div>
          </div>
        );
      })()}

      {/* Search */}
      {results.length > 0 && (
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Пошук по URL..."
          style={{ ...inp, fontSize: 12, padding: "6px 10px", marginBottom: 14 }} />
      )}

      {/* Loading */}
      {loading && <div style={{ textAlign: "center", padding: 30, color: t.textMuted }}>Завантаження...</div>}

      {/* Empty */}
      {!loading && grouped.length === 0 && (
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>⚡</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Немає даних PageSpeed</div>
          <div style={{ fontSize: 12, color: t.textMuted }}>Введіть URL або натисніть "Всі" для масової перевірки</div>
        </div>
      )}

      {/* Results */}
      {grouped.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", fontSize: 10, color: t.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
            <span style={{ flex: 1 }}>URL</span>
            <span style={{ width: 160, textAlign: "center" }}>📱 Mobile</span>
            <span style={{ width: 160, textAlign: "center" }}>🖥 Desktop</span>
          </div>

          {grouped.map((g) => {
            const isExp = expanded === g.url;
            const mob = g.mobile;
            const desk = g.desktop;
            return (
              <div key={g.url} style={{
                ...card, padding: 0, margin: 0, overflow: "hidden",
                border: `1px solid ${isExp ? "#6366f1" + "33" : t.border}`,
              }}>
                <div onClick={() => setExpanded(isExp ? null : g.url)} style={{
                  display: "flex", alignItems: "center", gap: 6, padding: "10px 14px", cursor: "pointer",
                }}>
                  <span style={{ fontSize: 11, color: isExp ? "#6366f1" : "#888", transform: isExp ? "rotate(90deg)" : "none", transition: "transform .2s" }}>▶</span>
                  <span style={{ fontSize: 12, fontWeight: 500, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{stripUrl(g.url)}</span>

                  {/* Mobile scores */}
                  <div style={{ width: 160, display: "flex", gap: 3, justifyContent: "center" }}>
                    {mob ? (
                      <>
                        <ScoreBadge value={mob.performance} />
                        <ScoreBadge value={mob.accessibility} />
                        <ScoreBadge value={mob.best_practices} />
                        <ScoreBadge value={mob.seo} />
                      </>
                    ) : <span style={{ fontSize: 10, color: t.textMuted }}>—</span>}
                  </div>

                  {/* Desktop scores */}
                  <div style={{ width: 160, display: "flex", gap: 3, justifyContent: "center" }}>
                    {desk ? (
                      <>
                        <ScoreBadge value={desk.performance} />
                        <ScoreBadge value={desk.accessibility} />
                        <ScoreBadge value={desk.best_practices} />
                        <ScoreBadge value={desk.seo} />
                      </>
                    ) : <span style={{ fontSize: 10, color: t.textMuted }}>—</span>}
                  </div>
                </div>

                {/* Expanded */}
                {isExp && (
                  <div style={{ padding: "12px 14px", borderTop: `1px solid ${t.border}`, background: dark ? "rgba(255,255,255,0.01)" : "rgba(0,0,0,0.01)" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      {["mobile", "desktop"].map(strat => {
                        const r = g[strat];
                        if (!r) return <div key={strat} style={{ textAlign: "center", padding: 20, color: t.textMuted, fontSize: 12 }}>Немає даних ({strat})</div>;
                        return (
                          <div key={strat}>
                            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 8, textAlign: "center" }}>
                              {strat === "mobile" ? "📱 Mobile" : "🖥 Desktop"}
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-around", marginBottom: 10 }}>
                              {[
                                { key: "performance", label: "Perf" },
                                { key: "accessibility", label: "A11y" },
                                { key: "best_practices", label: "BP" },
                                { key: "seo", label: "SEO" },
                              ].map(c => (
                                <div key={c.key} style={{ textAlign: "center" }}>
                                  <ScoreCircle value={r[c.key]} size={40} />
                                  <div style={{ fontSize: 8, color: t.textMuted, marginTop: 1 }}>{c.label}</div>
                                </div>
                              ))}
                            </div>
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                              {metricCard("FCP", r.fcp, r.fcp_display, 1800, 3000)}
                              {metricCard("LCP", r.lcp, r.lcp_display, 2500, 4000)}
                              {metricCard("CLS", r.cls, r.cls_display, 0.1, 0.25)}
                              {metricCard("TBT", r.tbt, r.tbt_display, 200, 600)}
                              {metricCard("SI", r.si, r.si_display, 3400, 5800)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ marginTop: 8, fontSize: 10, color: t.textMuted }}>
                      <a href={g.url} target="_blank" rel="noopener noreferrer" style={{ color: "#6366f1", textDecoration: "none" }}>{g.url}</a>
                      {(mob?.checked_at || desk?.checked_at) && <span style={{ marginLeft: 8 }}>Перевірено: {new Date(mob?.checked_at || desk?.checked_at).toLocaleString("uk-UA")}</span>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
